import React, { useCallback, useMemo } from 'react';
import { isDefined, isEmpty, mapHtmlPropsToReact, silenceError } from '@nfctron/nexus-core';
import { useLogger, type BasicComponent } from '@nfctron/nexus-react';
import { parse, type ElementNode, type Node } from 'svg-parser';

import { Sector } from '../Sector';
import { VirtualMap } from '../VirtualMap';
import type { VenueMapTypes as Types } from './VenueMap.types';

const transformSvgProps = (props: Record<string, unknown>) => {
	const transformed: Record<string, unknown> = {};
	const internalProps = ['sectorId', 'sectorTag', 'sectorName'];

	for (const [key, value] of Object.entries(props)) {
		/** skip internal props */
		if (internalProps.includes(key)) continue;
		transformed[key] = value;
	}

	return transformed;
};

/**
 * VenueMap component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const VenueMap: BasicComponent<Types.Props> = ({
	venue,
	categories,
	width,
	height,
	background = '#292929',
	forceBackground,
	onSectorSelect,
	onSectorUnselect,
	getIsSector,
	...virtualMapProps
}) => {
	const { loggerLow } = useLogger('VenueMap', { medium: true, high: true });

	/** parse the svg */
	const {
		rootSvg,
		baseWidth = width,
		baseHeight = height,
		rootBg = background,
	} = useMemo(() => {
		try {
			const parsed = parse(venue.drawing);
			const root = parsed.children.find((child) => child.type === 'element' && child.tagName === 'svg') as ElementNode;

			if (!root) {
				throw new Error('Invalid SVG: Root element not found');
			}

			const widthProp = silenceError(() => root.properties?.width ?? (root.properties?.viewBox as string)?.split(' ')[2], null);
			const heightProp = silenceError(() => root.properties?.height ?? (root.properties?.viewBox as string)?.split(' ')[3], null);
			const fillProp = root.properties?.fill?.toString();

			return {
				rootSvg: root,
				baseWidth: silenceError(() => parseInt(widthProp!.toString()), width),
				baseHeight: silenceError(() => parseInt(heightProp!.toString()), height),
				rootBg: forceBackground || fillProp === 'none' ? background : fillProp,
			};
		} catch (error) {
			loggerLow('Failed to parse SVG:', error);
			return {
				rootSvg: null,
				baseWidth: width,
				baseHeight: height,
				rootBg: background,
			};
		}
	}, [background, forceBackground, height, loggerLow, venue.drawing, width]);

	/** node child renderer callback */
	const renderNodeChild = useCallback(
		(childNode: Node | string, key: number): React.ReactNode => {
			if (typeof childNode === 'string') return null;

			const renderNode = (node: Node): React.ReactNode => {
				if (node.type === 'text') return null;

				node.properties = isDefined(node.properties) ? mapHtmlPropsToReact(node.properties) : {};

				switch (node.tagName) {
					case 'rect':
					case 'ellipse':
					case 'circle':
					case 'path': {
						const idAttr = node.properties?.id?.toString();

						if (isDefined(node.properties) && !isEmpty(idAttr) && idAttr.startsWith('sector:')) {
							const sector = venue.sectors.find((s) => s.categories?.length > 0 && s.tag === idAttr.replace('sector:', ''));

							if (!sector) return null;

							return (
								<Sector
									key={sector.sectorId ?? idAttr}
									tag={node.tagName}
									{...transformSvgProps(node.properties)}
									onSectorSelect={onSectorSelect}
									onSectorUnselect={onSectorUnselect}
									getIsSector={getIsSector}
									sector={{
										...sector,
										categories: categories.filter((c) => sector.categories.findIndex((sc) => sc.categoryId === c.categoryId) !== -1),
									}}
								>
									{node.children.map((child, index) => renderNodeChild(child, key + index))}
								</Sector>
							);
						}

						return React.createElement(
							node.tagName,
							{
								key: `path-${idAttr ?? key}`,
								...transformSvgProps(node.properties),
							},
							node.children.map((child, index) => renderNodeChild(child, key + index)),
						);
					}

					case 'g':
						return React.createElement(
							'g',
							{
								key: `g-${node.properties?.id ?? key}`,
								...transformSvgProps(node.properties),
							},
							node.children.map((child, index) => renderNodeChild(child, key + index)),
						);

					default:
						if (isEmpty(node.tagName)) return null;
						return React.createElement(
							node.tagName,
							{
								key: `svg-${node.properties?.id ?? key}`,
								...transformSvgProps(node.properties),
							},
							node.children.map((child, index) => renderNodeChild(child, key + index)),
						);
				}
			};

			return renderNode(childNode);
		},
		[categories, getIsSector, onSectorSelect, onSectorUnselect, venue.sectors],
	);

	if (!rootSvg) return null;

	return (
		<VirtualMap
			width={width}
			height={height}
			fill={rootBg}
			minScaleFactor={1.1}
			maxScaleFactor={0.1}
			baseHeight={baseHeight}
			baseWidth={baseWidth}
			{...virtualMapProps}
		>
			{rootSvg.children.map(renderNodeChild)}
		</VirtualMap>
	);
};

/** VenueMap component display name */
VenueMap.displayName = 'component.VenueMap';
